import { DateTime } from "luxon";
import { INV_TYPES } from "@/config/constants";
import { formatAmount } from "./utils/priceFormatter";

export const paymentsDataParser = (data: any) => {
  let formattedData: {
    type: string;
    price: number;
    unitNumber: string;
    location: string;
    date: string;
    id: string;
    label: string;
    imageUrl: string;
    payload: any;
  }[] = [];
  data.map((item: any) => {
    const updatedData: {
      type: string;
      price: number;
      unitNumber: string;
      location: string;
      date: string;
      id: string;
      label: string;
      imageUrl: string;
      payload: any;
    } = {
      type: getServiceType(item?.inv_type ?? ""),
      price: parseFloat(item?.balance_amount ?? 0),
      unitNumber: item?.unit_key,
      location: item?.community ?? "",
      date: getDateFormatter(item?.due_date ?? ""),
      id: item?.id_billing,
      label: getLabel(item?.inv_type),
      imageUrl: item?.imageUrl,
      payload: item,
    };
    formattedData.push(updatedData);
  });
  return formattedData;
};

const getLabel = (type: string): string => {
  const typeMapping: { [key: string]: string } = {
    SC: "Service Charges",
    AF: "Admin Fees",
    [INV_TYPES.PENDING_SERVICE_CHARGES]: "Pending Service Charges",
    [INV_TYPES.OA_NOC_FEE]: "No Objection Certificate Clearance Fees",
    [INV_TYPES.DEV_NOC_FEE]: "Developer No Objection Certificate Fees",
    NOC_SC: "Service Charges",
    Fines: "Other Fees",
  };
  return typeMapping[type] || "Service Charges";
};

const getServiceType = (type: string): string => {
  const typeMapping: { [key: string]: string } = {
    SC: "sc",
    AF: "af",
  };
  return typeMapping[type] || "";
};

/**
 * give date input as 2021-06-01
 * @param inputDate
 * @returns
 */
const getDateFormatter = (inputDate: string) => {
  // Parse the input date string into a Luxon DateTime object
  const dateTime = DateTime.fromSQL(inputDate);
  const formattedDate = dateTime.toFormat("dd-MM-yyyy");
  return formattedDate;
};

/**
 * give date as "2024-07-01 11:27:19"
 * @param inputDate
 * @returns "11:27 AM"
 */
const getTimeFromString = (inputDate: string) => {
  let dt = DateTime.fromFormat(inputDate, "yyyy-MM-dd HH:mm:ss", {
    zone: "utc",
  });
  dt = dt.toLocal();
  let formattedTime = dt.toFormat("hh:mm a");
  return formattedTime;
};

export const statementsDataParser = (data: any) => {
  const formattedData: any = [];
  data?.map((item: any) => {
    const updatedData: {
      type: string;
      price: string;
      unitNumber: string;
      location: string;
      date: string;
      id: string;
      label: string;
      transactionId: string;
    } = {
      type: getServiceType(item?.tran_type ?? "SC"),
      price: item?.paid_amount,
      unitNumber: item?.unit_key,
      location: item?.community ?? "",
      date: getDateFormatter(item?.created_on),
      id: item?.id_property,
      label: getLabel(item?.tran_type ?? "SC"),
      transactionId: item?.transaction_no,
    };
    formattedData.push(updatedData);
  });
  return formattedData;
};

export const statusDetailsParser = (data: any) => {
  return {
    totalPaid: 2343645,
    paymentType: "Service charges",
    darnaPoints: 5000,
    dateTime: "March 23, 2024, 08:30 PM ",
    location: "Unit 459, Al Zeina, Al Raha Beach ",
    referenceNumber: "122323.43435",
  };
};

export const cardDetailsParser = (data: any) => {
  return {
    type: getServiceType(data?.tran_type),
    price: data?.paid_amount,
    unitNumber: data?.unit_key,
    location: data?.community,
    date: getDateFormatter(data?.created_on),
    label: getLabel(data?.tran_type),
    imageUrl: data?.imageUrl,
    receiptNumber: data?.transaction_no,
  };
};

export const summaryDataParser = (data: any) => {
  return [
    {
      heading: "Summary",
      attributes: [
        {
          label: "Total paid",
          value: formatAmount(data?.paid_amount ?? ""),
        },
        { label: "Transaction Number", value: data?.transaction_no },
        { label: "Payment method", value: data?.payment_mode },
        { label: "Time", value: getTimeFromString(data?.created_on) },
        { label: "Reference Number", value: data?.referenceId },
      ],
    },
  ];
};

export const serviceChargesDataParser = (data: any) => {
  return [
    {
      heading: "Service charges breakdown",
      attributes: [
        { label: "Tax fees", value: "AED 2,000" },
        { label: "Admin fees", value: "AED 1,000" },
        { label: "Other fees", value: "AED 500" },
      ],
    },
  ];
};

export const parseFilterResponse = (data: any) => {
  let transformedOutput: any = {};
  // Transform paymentType
  transformedOutput.paymentType = data?.paymentType?.map((item: any) => ({
    value: item.key,
    label: item.value,
  }));

  // Transform paymentStatus
  transformedOutput.paymentstatus = data?.paymentstatus?.map((item: any) => ({
    value: item.key,
    label: item.value,
  }));

  // Transform communities
  transformedOutput.communities = data?.communities?.map((item: any) => ({
    value: item.value,
    label: item.key,
    unitList: item?.unitList ?? [],
  }));

  return transformedOutput;
};

export const parseSummaryDataResponse = (data: any) => {
  return {
    otherFees: data?.other_fee ?? 0,
    latePaymentFee: data?.penalty_amount ?? 0,
    totalAmount: data?.balance_amt ?? 0,
    unitName: data?.unit_key ?? "",
    community: data?.community ?? "",
    imageUrl: data?.imageUrl ?? "",
    dueDate: data?.due_date ?? "",
  };
};

function formatPaymentConfirmationTime(createdAt: string) {
  const date = new Date(createdAt);
   // Format the date as yyyy-mm-dd
   const formattedDate = date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).replace(/\//g, '-'); 
  const formattedTime = date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

  return `${formattedDate}, ${formattedTime}`;
}

export const parseStatusDetailsResponse = (data: any) => {
  return {
    totalAmount: data?.paid_amount,
    paymentType: data?.unit_details[0]
      ? getLabel(data?.unit_details[0]?.inv_type)
      : "",
    unitDetails: data?.unit_details ?? [],
    date: formatPaymentConfirmationTime(data?.created_at),
    referenceNumber: data?.reference_id,
    transactionNumber: data?.transaction_id,
  };
};
